<template>
  <section>
    <p class="text-sm text-gray-500 mb-5">
      Seuils de détection et destinataires des alertes (brute-force, refus d'accès répétés).
    </p>

      <div v-if="loading" class="py-16 text-center text-gray-400">Chargement…</div>
      <div v-else-if="loadError" class="py-16 text-center text-red-500">{{ loadError }}</div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <!-- ============ CONFIGURATION ============ -->
        <form @submit.prevent="saveConfig" class="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <p v-if="!canEdit" class="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            Lecture seule : seul l'administrateur global peut modifier ces réglages.
          </p>

          <label class="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" v-model="form.enabled" :disabled="!canEdit" class="w-4 h-4" />
            <span class="text-sm font-semibold text-gray-700">Alertes activées</span>
          </label>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Destinataires</label>
            <textarea v-model="recipientsText" :disabled="!canEdit" rows="3"
              placeholder="dsi@gouv.bj, securite@gouv.bj"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"></textarea>
            <p class="text-xs text-gray-400 mt-1">
              Séparés par des virgules ou des retours à la ligne. Vide : les alertes restent
              visibles dans le journal d'audit et Sentry, sans email.
            </p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div v-for="f in numberFields" :key="f.key">
              <label class="block text-sm font-semibold text-gray-700 mb-1">{{ f.label }}</label>
              <input type="number" min="1" v-model.number="form[f.key]" :disabled="!canEdit"
                class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
              <p class="text-xs text-gray-400 mt-1">{{ f.help }}</p>
            </div>
          </div>

          <p v-if="updatedAt" class="text-xs text-gray-400">
            Dernière modification le {{ formatDate(updatedAt) }}<span v-if="updatedBy"> par {{ updatedBy }}</span>.
          </p>

          <div v-if="canEdit" class="flex flex-wrap items-center gap-3 pt-2">
            <button type="submit" :disabled="saving"
              class="px-5 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50">
              {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
            </button>
            <button type="button" @click="sendTest" :disabled="testing || savedRecipients.length === 0"
              class="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
              :title="savedRecipients.length === 0 ? 'Enregistrez d\'abord au moins un destinataire' : ''">
              <Mail class="w-4 h-4" />{{ testing ? 'Envoi…' : 'Envoyer un email de test' }}
            </button>
            <span v-if="message" class="text-emerald-600 text-sm flex items-center gap-1">
              <Check class="w-4 h-4" /> {{ message }}
            </span>
            <span v-if="error" class="text-red-500 text-sm">{{ error }}</span>
          </div>
        </form>

        <!-- ============ DERNIÈRES ALERTES ============ -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">
            Dernières alertes envoyées
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                <th class="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                <th class="px-4 py-3 font-semibold">Type</th>
                <th class="px-4 py-3 font-semibold">Cible</th>
                <th class="px-4 py-3 font-semibold text-right">Nombre</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(a, idx) in recentAlerts" :key="idx" class="border-t border-gray-100">
                <td class="px-4 py-3 text-gray-500 whitespace-nowrap">{{ formatDate(a.created_at) }}</td>
                <td class="px-4 py-3">{{ typeLabels[a.type || ''] || a.type }}</td>
                <td class="px-4 py-3 font-mono text-xs">{{ a.type === 'connexions_echouees' ? `IP ${a.cle}` : `compte #${a.cle}` }}</td>
                <td class="px-4 py-3 text-right">{{ a.nombre }} <span class="text-gray-400">/ {{ a.fenetre_minutes }} min</span></td>
              </tr>
              <tr v-if="recentAlerts.length === 0">
                <td colspan="4" class="px-4 py-10 text-center text-gray-400">Aucune alerte envoyée.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { Check, Mail } from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'

type NumberKey = 'window_minutes' | 'login_failures' | 'otp_failures' | 'access_denials' | 'silence_minutes'

interface RecentAlert {
  created_at: string
  type: string | null
  cle: string | null
  nombre: number | null
  fenetre_minutes: number | null
}

interface AlertConfig extends Record<NumberKey, number> {
  enabled: boolean
  recipients: string[]
  updated_at: string | null
  updated_by: string | null
  can_edit: boolean
  recent_alerts: RecentAlert[]
}

const numberFields: { key: NumberKey; label: string; help: string }[] = [
  { key: 'login_failures', label: 'Échecs de connexion', help: 'par adresse IP, dans la fenêtre' },
  { key: 'otp_failures', label: 'Échecs de code OTP', help: 'par compte, dans la fenêtre' },
  { key: 'access_denials', label: "Refus d'accès", help: 'par compte, dans la fenêtre' },
  { key: 'window_minutes', label: "Fenêtre d'analyse (min)", help: 'période glissante observée' },
  { key: 'silence_minutes', label: 'Silence (min)', help: 'délai avant de renvoyer la même alerte' },
]

const typeLabels: Record<string, string> = {
  connexions_echouees: 'Échecs de connexion',
  otp_echoue: 'Échecs OTP',
  acces_refuses: "Refus d'accès",
}

const loading = ref(false)
const loadError = ref<string | null>(null)
const saving = ref(false)
const testing = ref(false)
const message = ref<string | null>(null)
const error = ref<string | null>(null)

const canEdit = ref(false)
const updatedAt = ref<string | null>(null)
const updatedBy = ref<string | null>(null)
const savedRecipients = ref<string[]>([])
const recentAlerts = ref<RecentAlert[]>([])
const recipientsText = ref('')
const form = reactive<Record<NumberKey, number> & { enabled: boolean }>({
  enabled: true, window_minutes: 5, login_failures: 10, otp_failures: 5, access_denials: 5, silence_minutes: 60,
})

const recipients = computed(() =>
  recipientsText.value.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean)
)

function apply(data: AlertConfig) {
  form.enabled = data.enabled
  for (const f of numberFields) form[f.key] = data[f.key]
  savedRecipients.value = data.recipients || []
  recipientsText.value = savedRecipients.value.join(', ')
  canEdit.value = data.can_edit
  updatedAt.value = data.updated_at
  updatedBy.value = data.updated_by
  recentAlerts.value = data.recent_alerts || []
}

function flash(text: string) {
  message.value = text
  setTimeout(() => { message.value = null }, 3000)
}

async function fetchConfig() {
  loading.value = true
  try {
    apply(await apiFetch<AlertConfig>('/api/admin/security-alerts/config/'))
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    loading.value = false
  }
}

async function saveConfig() {
  saving.value = true
  error.value = null
  try {
    const payload: Record<string, unknown> = { enabled: form.enabled, recipients: recipients.value }
    for (const f of numberFields) payload[f.key] = form[f.key]
    apply(await apiFetch<AlertConfig>('/api/admin/security-alerts/config/', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }))
    flash('Enregistré')
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    saving.value = false
  }
}

async function sendTest() {
  testing.value = true
  error.value = null
  try {
    const data = await apiFetch<{ sent_to: string[] }>('/api/admin/security-alerts/test/', { method: 'POST' })
    flash(`Email de test envoyé à ${data.sent_to.join(', ')}`)
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'envoi."
  } finally {
    testing.value = false
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

onMounted(fetchConfig)
</script>
